import { UserExport } from '@mark43/cad-api';
import { useEffect, useRef, useState } from 'react';
import { useBroadcast } from '../common/broadcast-channel/use-broadcast';

type ExportNotifications = {
    events: {
        onClearAllExports: () => void;
        onDownloadAllExports: () => void;
        onPopoverClose: () => void;
        onDownloadExport: (exportId: string) => void;
        onClearExport: (exportId: string) => void;
    };
    state: {
        notificationCount: number;
    };
};

export function useExportNotifications(currentExports: UserExport[]): ExportNotifications {
    const [exportNotificationMap, setMap] = useState<Record<string, boolean>>({});

    const exportNotificationsRef = useRef(exportNotificationMap);
    const notificationCount = Object.values(exportNotificationMap).filter((seen) => !seen).length;

    const { postMessage } = useBroadcast('export-notifications', (message) => {
        if (message.type === 'CLEAR_ALL_EXPORT_NOTIFICATIONS') {
            clearAllNotifications();
        }
        if (message.type === 'MARK_ALL_EXPORTS_AS_SEEN') {
            updateAllNotificationsToBeSeen();
        }
        if (message.type === 'MARK_EXPORT_AS_SEEN') {
            viewSingleNotification(message.payload.exportId);
        }
        if (message.type === 'CLEAR_EXPORT') {
            clearSingleNotification(message.payload.exportId);
        }
        if (message.type === 'INITIAL_STATE_REQUEST') {
            broadcastInitialState();
        }
        if (message.type === 'INITIAL_STATE_RESPONSE') {
            updateNotificationMap(message.payload.state);
        }
    });

    useEffect(
        function setUnseenNotifications() {
            const newMap = { ...exportNotificationsRef.current };
            for (const ex of currentExports) {
                // Is new unseen export
                if (newMap[ex.id] === undefined) {
                    newMap[ex.id] = false;
                }
            }
            // Don't use updateNotificationMap to avoid infinite loop
            exportNotificationsRef.current = newMap;
            setMap(newMap);
        },
        [currentExports]
    );

    // Prevents a new window from being out of sync
    useEffect(
        function syncWindows() {
            postMessage({ type: 'INITIAL_STATE_REQUEST' });
        },
        [postMessage]
    );

    function broadcastInitialState() {
        postMessage({ type: 'INITIAL_STATE_RESPONSE', payload: { state: exportNotificationMap } });
    }

    function updateNotificationMap(newMap: Record<string, boolean>) {
        exportNotificationsRef.current = newMap;
        setMap(newMap);
    }

    function clearAllNotifications() {
        updateNotificationMap({});
    }

    function updateAllNotificationsToBeSeen() {
        const newMap = { ...exportNotificationMap };
        Object.keys(newMap).forEach((id) => {
            newMap[id] = true;
        });

        updateNotificationMap(newMap);
    }

    function clearSingleNotification(exportId: string) {
        const newMap = { ...exportNotificationMap };
        delete newMap[exportId];
        updateNotificationMap(newMap);
    }

    function viewSingleNotification(exportId: string) {
        const newMap = { ...exportNotificationMap };
        newMap[exportId] = true;
        updateNotificationMap(newMap);
    }

    function clearNotifications() {
        postMessage({ type: 'CLEAR_ALL_EXPORT_NOTIFICATIONS' });
        clearAllNotifications();
    }

    function onClearExport(exportId: string) {
        postMessage({ type: 'CLEAR_EXPORT', payload: { exportId } });
        clearSingleNotification(exportId);
    }

    function onDownloadExport(exportId: string) {
        postMessage({ type: 'MARK_EXPORT_AS_SEEN', payload: { exportId } });
        viewSingleNotification(exportId);
    }

    function markAllNotificationsSeen() {
        postMessage({ type: 'MARK_ALL_EXPORTS_AS_SEEN' });
        updateAllNotificationsToBeSeen();
    }

    return {
        events: {
            onClearAllExports: clearNotifications,
            onDownloadAllExports: markAllNotificationsSeen,
            onPopoverClose: markAllNotificationsSeen,
            onDownloadExport,
            onClearExport,
        },
        state: {
            notificationCount,
        },
    };
}
